import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import useFocusTimeSlots from 'domain/people/availability/focusTime/useFocusTimeSlots';
import { areSlotsEqual } from 'domain/people/availability/focusTime/helpers/helpers';
import useSyberryTodayService from 'domain/people/syberryToday/syberryTodayService';
import useSyberryTodayPlanningState from 'domain/people/syberryToday/state';
import type { SyberryTodaySyncPlanningOptions } from 'domain/people/syberryToday/state/types';
import {
  SyberryTodayPersonalBacklogCategory,
  SyberryTodayPlanStatus,
  type SyberryTodayPlanning,
  type SyberryTodayProjectAllocationSummary,
  type SyberryTodayTaskId,
} from 'domain/people/syberryToday/types';
import type { FocusTimeSlot } from 'domain/people/availability/focusTime/types';
import { getSelectedGroupProject } from './utils';
import type { UseSyberryTodayPageControllerResult } from './types';
import useSyberryTodaySystemWarning from './useSyberryTodaySystemWarning';
import useSyberryTodayTaskOpenedSync from './useSyberryTodayTaskOpenedSync';

enum SyberryTodayPendingAction {
  SAVING = 'SAVING',
  CONFIRMING = 'CONFIRMING',
  POSTPONING = 'POSTPONING',
  SKIPPING = 'SKIPPING',
}

const SAVE_TASK_SELECTION_ERROR_MESSAGE = 'Could not save your task selection. Please try again.';
const MARK_TASK_OPENED_ERROR_MESSAGE = 'Could not mark the task as opened. Please try again.';
const CONFIRM_CHECK_IN_ERROR_MESSAGE = 'Could not confirm your check-in. Please try again.';
const POSTPONE_PLANNING_ERROR_MESSAGE = 'Could not postpone planning. Please try again.';
const SKIP_CHECK_IN_ERROR_MESSAGE = 'Could not skip check-in. Please try again.';
const LOAD_PLANNING_ERROR_MESSAGE = 'Could not load your planning. Please try again.';
const LOAD_ALLOCATION_ERROR_MESSAGE = 'Could not load allocation guidance. Please try again.';

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const useSyberryTodayPageController = (): UseSyberryTodayPageControllerResult => {
  const {
    confirmCheckIn,
    getTodayAllocations,
    getTodayPlanning,
    markTaskAsOpened,
    postponePlanning,
    saveTaskSelection,
    skipCheckInCompletely,
  } = useSyberryTodayService();
  const { addMessage } = useMessageService();
  const addMessageRef = useRef(addMessage);
  const { currentMinutes: focusTimeCurrentMinutes, saveSlots, slots: focusTimeSlots } = useFocusTimeSlots();
  const [allocations, setAllocations] = useState<SyberryTodayProjectAllocationSummary[]>([]);
  const [focusTimeDraftSlots, setFocusTimeDraftSlots] = useState<FocusTimeSlot[]>(focusTimeSlots);
  const [isAllocationLoading, setIsAllocationLoading] = useState(true);
  const [isAllocationError, setIsAllocationError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<Nullable<SyberryTodayPendingAction>>(null);
  const [dashboardSelectionTaskId, setDashboardSelectionTaskId] = useState<Nullable<SyberryTodayTaskId>>(null);
  const lastSyncedPlanningRef = useRef<Nullable<SyberryTodayPlanning>>(null);
  const syncSystemWarning = useSyberryTodaySystemWarning();
  const { state, activeStep, selectedTasks, canConfirmTaskSelection, canConfirmCheckIn, actions } =
    useSyberryTodayPlanningState();

  useEffect(() => {
    addMessageRef.current = addMessage;
  }, [addMessage]);

  useEffect(() => {
    setFocusTimeDraftSlots(focusTimeSlots);
  }, [focusTimeSlots]);

  const showErrorMessage = useCallback((message: string) => {
    addMessageRef.current(new ErrorMessage(message));
  }, []);

  const recoverPlanning = useCallback(
    async (message: string, fallbackErrorMessage: string) => {
      try {
        const recoveryPlanning = await getTodayPlanning();

        lastSyncedPlanningRef.current = recoveryPlanning;
        syncSystemWarning(recoveryPlanning.systemWarning);
        actions.syncPlanning(recoveryPlanning);
        showErrorMessage(message);
      } catch (error) {
        showErrorMessage(fallbackErrorMessage);
      }
    },
    [actions, getTodayPlanning, showErrorMessage, syncSystemWarning],
  );

  const handleMarkTaskAsOpenedError = useCallback(
    (error: unknown) =>
      recoverPlanning(getErrorMessage(error, MARK_TASK_OPENED_ERROR_MESSAGE), MARK_TASK_OPENED_ERROR_MESSAGE),
    [recoverPlanning],
  );

  const { queueMarkTaskAsOpened, waitForTaskOpenedRequests } = useSyberryTodayTaskOpenedSync({
    markTaskAsOpened,
    onMarkTaskAsOpenedError: handleMarkTaskAsOpenedError,
  });

  const runAction = useCallback(
    async (key: SyberryTodayPendingAction, fn: () => Promise<void>, fallbackErrorMessage: string) => {
      if (pendingAction) return;

      setPendingAction(key);
      try {
        await fn();
      } catch (error) {
        showErrorMessage(fallbackErrorMessage);
      } finally {
        setPendingAction(null);
      }
    },
    [pendingAction, showErrorMessage],
  );

  const syncAfterPlanningMutation = useCallback(
    async (
      mutate: () => Promise<SyberryTodayPlanning>,
      fallbackErrorMessage: string,
      syncOptions?: SyberryTodaySyncPlanningOptions,
    ) => {
      try {
        const syncedPlanning = await mutate();

        lastSyncedPlanningRef.current = syncedPlanning;
        syncSystemWarning(syncedPlanning.systemWarning);
        actions.syncPlanning(syncedPlanning, syncOptions);
      } catch (error) {
        await recoverPlanning(getErrorMessage(error, fallbackErrorMessage), fallbackErrorMessage);
      }
    },
    [actions, recoverPlanning, syncSystemWarning],
  );

  useEffect(() => {
    let isMounted = true;

    getTodayPlanning()
      .then(todayPlanning => {
        if (!isMounted) return;

        lastSyncedPlanningRef.current = todayPlanning;
        syncSystemWarning(todayPlanning.systemWarning);
        actions.syncPlanning(todayPlanning);
      })
      .catch(() => {
        if (!isMounted) return;

        showErrorMessage(LOAD_PLANNING_ERROR_MESSAGE);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [actions, getTodayPlanning, showErrorMessage, syncSystemWarning]);

  useEffect(() => {
    let isMounted = true;

    setIsAllocationLoading(true);
    setIsAllocationError(false);

    getTodayAllocations()
      .then(todayAllocations => {
        if (!isMounted) return;

        setAllocations(todayAllocations);
      })
      .catch(() => {
        if (!isMounted) return;

        setAllocations([]);
        setIsAllocationError(true);
        showErrorMessage(LOAD_ALLOCATION_ERROR_MESSAGE);
      })
      .finally(() => {
        if (isMounted) setIsAllocationLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [getTodayAllocations, showErrorMessage]);

  const activeGroup = state.groups.find(group => group.id === state.activeGroupId) ?? null;
  const activeProject = getSelectedGroupProject(state.projects, activeGroup);
  const allocationByProjectId = useMemo(
    () => new Map(allocations.map(allocation => [allocation.projectId, allocation])),
    [allocations],
  );
  const activeAllocationSummary = activeProject ? (allocationByProjectId.get(activeProject.id) ?? null) : null;
  const activeStreams = useMemo(
    () => state.streams.filter(stream => stream.groupId === state.activeGroupId),
    [state.activeGroupId, state.streams],
  );
  const activeTasks = useMemo(
    () => state.tasks.filter(task => task.groupId === state.activeGroupId),
    [state.activeGroupId, state.tasks],
  );

  const handleConfirmTaskSelection = useCallback(async () => {
    if (!canConfirmTaskSelection) return;

    await runAction(
      SyberryTodayPendingAction.SAVING,
      () =>
        syncAfterPlanningMutation(async () => {
          await waitForTaskOpenedRequests();

          return saveTaskSelection(state.selectedTaskIds, lastSyncedPlanningRef.current ?? undefined);
        }, SAVE_TASK_SELECTION_ERROR_MESSAGE),
      SAVE_TASK_SELECTION_ERROR_MESSAGE,
    );
  }, [
    canConfirmTaskSelection,
    runAction,
    saveTaskSelection,
    state.selectedTaskIds,
    syncAfterPlanningMutation,
    waitForTaskOpenedRequests,
  ]);

  const handleConfirmCheckIn = useCallback(async () => {
    if (!canConfirmCheckIn) return;

    await runAction(
      SyberryTodayPendingAction.CONFIRMING,
      async () => {
        await waitForTaskOpenedRequests();

        if (!areSlotsEqual(focusTimeSlots, focusTimeDraftSlots)) {
          await saveSlots(focusTimeDraftSlots, { showErrorToast: false, showSuccessToast: false });
        }

        await syncAfterPlanningMutation(
          () => confirmCheckIn({ taskIds: state.selectedTaskIds }),
          CONFIRM_CHECK_IN_ERROR_MESSAGE,
        );
      },
      CONFIRM_CHECK_IN_ERROR_MESSAGE,
    );
  }, [
    canConfirmCheckIn,
    confirmCheckIn,
    focusTimeDraftSlots,
    focusTimeSlots,
    runAction,
    saveSlots,
    state.selectedTaskIds,
    syncAfterPlanningMutation,
    waitForTaskOpenedRequests,
  ]);

  const handleDashboardTaskSelectionToggle = useCallback(
    async (taskId: SyberryTodayTaskId) => {
      if (pendingAction) return;

      const nextSelectedTaskIds = state.selectedTaskIds.includes(taskId)
        ? state.selectedTaskIds.filter(selectedTaskId => selectedTaskId !== taskId)
        : [...state.selectedTaskIds, taskId];

      setDashboardSelectionTaskId(taskId);
      try {
        await runAction(
          SyberryTodayPendingAction.SAVING,
          () =>
            syncAfterPlanningMutation(async () => {
              await waitForTaskOpenedRequests();

              return saveTaskSelection(nextSelectedTaskIds, lastSyncedPlanningRef.current ?? undefined);
            }, SAVE_TASK_SELECTION_ERROR_MESSAGE),
          SAVE_TASK_SELECTION_ERROR_MESSAGE,
        );
      } finally {
        setDashboardSelectionTaskId(null);
      }
    },
    [
      pendingAction,
      runAction,
      saveTaskSelection,
      state.selectedTaskIds,
      syncAfterPlanningMutation,
      waitForTaskOpenedRequests,
    ],
  );

  const handleTaskExpansionToggle = useCallback(
    (taskId: SyberryTodayTaskId) => {
      const task = state.tasks.find(currentTask => currentTask.id === taskId);
      const shouldMarkTaskAsOpened =
        pendingAction === null &&
        state.expandedTaskId !== taskId &&
        !task?.selectedForToday &&
        task?.personalBacklogCategory === SyberryTodayPersonalBacklogCategory.INBOX;

      actions.toggleTaskExpansion(taskId);

      if (shouldMarkTaskAsOpened) {
        queueMarkTaskAsOpened(taskId);
      }
    },
    [actions, pendingAction, queueMarkTaskAsOpened, state.expandedTaskId, state.tasks],
  );

  const isSavingTaskSelection = pendingAction === SyberryTodayPendingAction.SAVING;
  const isConfirmingCheckIn = pendingAction === SyberryTodayPendingAction.CONFIRMING;
  const isPostponing = pendingAction === SyberryTodayPendingAction.POSTPONING;
  const isSkipping = pendingAction === SyberryTodayPendingAction.SKIPPING;
  const isSkipCheckInActionInProgress = isPostponing || isSkipping;
  const shouldShowDashboard = [
    SyberryTodayPlanStatus.CONFIRMED,
    SyberryTodayPlanStatus.POSTPONED,
    SyberryTodayPlanStatus.SKIPPED,
  ].includes(state.planStatus);

  const handleCloseSkipCheckInModal = useCallback(() => {
    if (isSkipCheckInActionInProgress) return;

    actions.closeSkipCheckInModal();
  }, [actions, isSkipCheckInActionInProgress]);

  const handleConfirmPostponePlanning = useCallback(async () => {
    await runAction(
      SyberryTodayPendingAction.POSTPONING,
      () =>
        syncAfterPlanningMutation(postponePlanning, POSTPONE_PLANNING_ERROR_MESSAGE, {
          closeSkipCheckInModal: true,
        }),
      POSTPONE_PLANNING_ERROR_MESSAGE,
    );
  }, [postponePlanning, runAction, syncAfterPlanningMutation]);

  const handleSkipCheckInCompletely = useCallback(async () => {
    await runAction(
      SyberryTodayPendingAction.SKIPPING,
      () =>
        syncAfterPlanningMutation(skipCheckInCompletely, SKIP_CHECK_IN_ERROR_MESSAGE, {
          closeSkipCheckInModal: true,
        }),
      SKIP_CHECK_IN_ERROR_MESSAGE,
    );
  }, [runAction, skipCheckInCompletely, syncAfterPlanningMutation]);

  return {
    isLoading,
    activeStep,
    shouldShowDashboard,
    dashboard: {
      tasks: state.tasks,
      todayTasks: selectedTasks,
      groups: state.groups,
      planStatus: state.planStatus,
      expandedTaskId: state.expandedTaskId,
      selectionPendingTaskId: dashboardSelectionTaskId,
      isSelectionDisabled: pendingAction !== null,
      onToggleSelection: handleDashboardTaskSelectionToggle,
      onToggleExpansion: handleTaskExpansionToggle,
    },
    planningFlow: {
      selectPanel: {
        activeStep,
        groups: state.groups,
        activeGroupId: state.activeGroupId,
        canConfirmTaskSelection: canConfirmTaskSelection && !isSavingTaskSelection,
        canConfirmCheckIn: canConfirmCheckIn && !isConfirmingCheckIn,
        isSkipCheckInDisabled: pendingAction !== null,
        onSelectGroup: actions.setActiveGroup,
        onConfirmTaskSelection: handleConfirmTaskSelection,
        onBackToTaskSelection: actions.backToTaskSelection,
        onConfirmCheckIn: handleConfirmCheckIn,
        onOpenSkipCheckInModal: actions.openSkipCheckInModal,
      },
      board: {
        activeGroup,
        activeProject,
        allocationSummary: activeAllocationSummary,
        isAllocationLoading,
        isAllocationError,
        streams: activeStreams,
        tasks: activeTasks,
        selectedTasks,
        focusTimeCurrentMinutes,
        focusTimeSlots: focusTimeDraftSlots,
        expandedTaskId: state.expandedTaskId,
        onChangeFocusTimeSlots: setFocusTimeDraftSlots,
        onToggleSelection: actions.toggleTaskSelection,
        onToggleExpansion: handleTaskExpansionToggle,
      },
      skipCheckIn: {
        isOpen: state.isSkipCheckInModalOpen,
        isPostponing,
        isSkipping,
        onClose: handleCloseSkipCheckInModal,
        onConfirmPostpone: handleConfirmPostponePlanning,
        onSkipCompletely: handleSkipCheckInCompletely,
      },
    },
  };
};

export default useSyberryTodayPageController;
